import { set, get } from "lodash";

type MapValuesInput = {
  data: { [key: string]: any };
  valuesMap: { [key: string]: string };
  direction?: "in" | "out";
  mapFunctions?: { [key: string]: (any) => any };
  normalizeUndefined?: boolean;
};

const setterFn = <T>(ac, path, value, mapFunction) => {
  if (Array.isArray(value) && mapFunction) {
    return set<T>(ac, path, value.map(mapFunction));
  }

  return set<T>(ac, path, value);
};

const getterFn = (data, path, mapFunction, normalize) => {
  const value = get(data, path);

  if (mapFunction) {
    return Array.isArray(value) ? value.map(mapFunction) : mapFunction(value);
  }

  return normalize(value);
};

/**
 * Maps values "in" to a flattened structure or
 * "out" to a nested object structure
 *
 * Note that undefined properties are normalized
 * to empty string on the way in, as this is useful
 * when setting default form inputs
 *
 * @todo: pass in default value per path instead
 */
export const mapValues = <T extends object>({
  data,
  valuesMap,
  direction = "in",
  mapFunctions = {},
  normalizeUndefined = true,
}: MapValuesInput): T =>
  Object.entries(valuesMap).reduce((ac: T, entry) => {
    const setPath = entry[direction === "in" ? 0 : 1];
    const getPath = entry[direction === "in" ? 1 : 0];
    const normalize = direction === "in" ? (v) => (v === undefined && normalizeUndefined ? "" : v) : (v) => v;
    return setterFn<T>(ac, setPath, getterFn(data, getPath, mapFunctions[getPath], normalize), mapFunctions[setPath]);
  }, {} as T);

/**
 * Filters a map object down to a subset of the map by the presence of a substring in the map keys
 */
export const filterMap = (valuesMap: MapValuesInput["valuesMap"], groupFilter: string, exclude = "") =>
  Object.fromEntries(Object.entries(valuesMap).filter(([key]) => exclude !== key && key.includes(groupFilter)));
